import { useStripeConfirm } from '#src/app/shared/hooks/payments';
import type { WithTokenParams } from '#src/app/shared/types/payment';
import { metaPixelTracking } from '#src/app/shared/utils/metaPixel';
import { paymentMethod } from '#src/app/shared/utils/payments';

import { usePromoteFormData } from '../../../formContext/formData';
import { usePromoteFormState } from '../../../formContext/formState';
import { useCreatePromotion } from '../useCreatePromotion';
import { useNavigateToThankYouPage } from '../useNavigateToThankYouPage';
import { usePromotionChargePaymentMethod } from '../usePaymentMethodFunctions';
import { usePPMU } from '../usePPMU';
import { useTracking } from '../useTracking';
import { useTrackModelSuccess } from '../useTrackModelSuccess';

import { useHandleError } from './hooks/useHandleError';

export function useSubmit(): (params: WithTokenParams) => Promise<void> {
	const [{ amount }, { setLoading }] = usePromoteFormState();
	const { currency } = usePromoteFormData();

	const { trackSubmitValid } = useTracking();

	const createPromotion = useCreatePromotion();
	const navigateToThankYouPage = useNavigateToThankYouPage();
	const confirmStripe = useStripeConfirm();
	// useButtonBanditReward and useComboBanditReward skipped - bandits not available in formData

	const processPPMU = usePPMU();

	const promotionChargePaymentMethod = usePromotionChargePaymentMethod();
	const handleError = useHandleError();

	const trackModelSuccess = useTrackModelSuccess();

	return async function submit({ paymentType, token, cardType, onTransactionComplete }) {
		trackSubmitValid({ paymentType });
		setLoading();
		try {
			const result = await createPromotion(promotionChargePaymentMethod(paymentType, token), paymentType);
			await confirmStripe(result, paymentMethod(paymentType, token));
			onTransactionComplete && onTransactionComplete('success');
			trackModelSuccess({
				paymentType,
				cardType,
			});
			metaPixelTracking.trackPurchase({ value: amount, currency: currency.code });
			// rewardButtonBandit and rewardComboBandit skipped - bandits not available
			const ppmuResult = await processPPMU({
				token: result?.paymentMethodId || token,
				accountId: result?.accountId,
				paymentType,
			});
			if (ppmuResult?.standalonePage) return;
			navigateToThankYouPage();
		} catch (error: unknown) {
			handleError(error, paymentType, onTransactionComplete);
		}
	};
}
